import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Form, Input, Select } from 'antd'
import Modal from 'antd/lib/modal/Modal'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { FileUploadButton } from '@/components/file-upload-button/file-upload-button.component'
import {
  AnnouncementType,
  GetAnnouncementPostsDocument,
  GetTimelinePostsDocument,
  RequestedPostFragment,
  useAnnouncementCreateMutation,
  useAnnouncementUpdateMutation,
} from '@/generated/graphql'

export const AnnouncementPostModal: React.FC<{ post?: RequestedPostFragment }> = ({ post, children, ...props }) => {
  const [visible, { toggle }] = useToggle()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [fileIds, setFileIds] = React.useState<number[]>((post?.files || []).map((file) => file.fileId))

  React.useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible])

  const refetchQueries = {
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!, getOperationName(GetAnnouncementPostsDocument)!],
  }

  const [announcementCreate] = useAnnouncementCreateMutation({ ...refetchQueries })
  const [announcementUpdate] = useAnnouncementUpdateMutation({ ...refetchQueries })

  const initialValues = post
    ? {
        title: post.announcement?.title,
        content: post.announcement?.content,
        type: post.announcement?.type,
      }
    : {
        title: '',
        content: '',
        type: AnnouncementType.Announcement,
      }

  children = React.cloneElement(children as any, {
    ...props,
    onClick: () => toggle(),
  })

  const onOK = async () => {
    const values = await form.validateFields()
    if (!post) {
      await announcementCreate({
        variables: {
          input: {
            content: values.content,
            title: values.title,
            type: values.type,
            fileIds,
          },
        },
      })
    } else {
      await announcementUpdate({
        variables: {
          input: {
            postId: post.postId,
            announcementId: post.announcement!.announcementId,
            content: values.content,
            title: values.title,
            type: values.type,
            fileIds,
          },
        },
      })
    }
    toggle()
  }

  const formComponent = (
    <Form form={form} initialValues={initialValues}>
      <Form.Item name='title' label={t('post.announcement.modal.title')} rules={[{ required: true }]}>
        <Input allowClear />
      </Form.Item>
      <Form.Item name='type' label={t('post.announcement.modal.type')} rules={[{ required: true }]}>
        <Select>
          <Select.Option value={AnnouncementType.Announcement}>
            {t(`post.announcement.type.${AnnouncementType.Announcement}`)}
          </Select.Option>
          <Select.Option value={AnnouncementType.Feature}>
            {t(`post.announcement.type.${AnnouncementType.Feature}`)}
          </Select.Option>
          <Select.Option value={AnnouncementType.Fix}>
            {t(`post.announcement.type.${AnnouncementType.Fix}`)}
          </Select.Option>
          <Select.Option value={AnnouncementType.Improvement}>
            {t(`post.announcement.type.${AnnouncementType.Improvement}`)}
          </Select.Option>
        </Select>
      </Form.Item>
      <Form.Item name='content' label={t('post.announcement.modal.content')} rules={[{ required: true }]}>
        <Input.TextArea allowClear />
      </Form.Item>
      <FileUploadButton
        currentFiles={post?.files}
        isDone={!visible}
        name='announcementFileUpload'
        setFileIds={setFileIds}
      />
    </Form>
  )
  return (
    <>
      {children}
      <Modal
        title={t('post.announcement.modal.header')}
        visible={visible}
        onCancel={() => toggle()}
        onOk={onOK}
        destroyOnClose
      >
        {formComponent}
      </Modal>
    </>
  )
}
